import {ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {CommonModule, DecimalPipe} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {ToastService} from "../services/Toast.service";
import {FileUploader, FileUploaderOptions, FileUploadModule} from "ng2-file-upload";
import {LoadingService} from "../services/LoadingService";
import {TOAST_TYPE} from "../constants/Tpiq.constants";
import {ManageTaxStrategiesServices} from "../tpiq-services/manage-tax-strategies.service";
import {Session} from "../services/Session";
import {v4 as uuid} from 'uuid';
import S3 from 'aws-sdk/clients/s3';
import {MatIcon} from "@angular/material/icon";
import { MatProgressBarModule } from '@angular/material/progress-bar';

// Questionnaire attachments accept documents and photos only — never
// executables or scripts (CU-868kg6qux). Default-deny allowlist enforced
// client-side here and again server-side at the attach endpoint.
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg', 'heic'];
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/png', 'image/jpeg', 'image/heic'
];
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

@Component({
  selector: 'app-tpiq-file-upload',
  standalone: true,
  imports: [CommonModule, FormsModule, DecimalPipe, FileUploadModule, MatIcon, MatProgressBarModule],
  templateUrl: './tpiq-file-upload.component.html',
  styleUrl: './tpiq-file-upload.component.scss'
})
export class TpiqFileUploadComponent {
  @Output() onClose: EventEmitter<void> = new EventEmitter<void>();
  @Output() onFileUpload: EventEmitter<any> = new EventEmitter<any>();
  @Input() id: string;
  // bound to the file input's accept attribute in the template
  @Input() acceptTypes = '.pdf,.doc,.docx,.png,.jpg,.jpeg,.heic';

  @Input()
  set s3Credentials(s3details) {
    this.setS3Details(s3details);
  }

  uploadBucket: string;
  s3Data: any = {};
  totalSize = 0;
  totalFiles = 0;
  loaded = [];
  progressWidth = 0;
  bulkUploadCount = 0;
  showProgress: boolean;
  selectedFiles: Array<any> = [];
  uploadedFiles: Array<any> = [];

  uploader: FileUploader;
  org: any;
  user: any;
  uploadCount = 0;

  constructor(private changeDetectorRef: ChangeDetectorRef,
              private taxStrategyServices: ManageTaxStrategiesServices,
              private toastService: ToastService,
              private loadingService: LoadingService,
  ) {
    this.org = Session.getOrg();
    this.user = Session.getUser().id;
    this.uploaderUpdate();
    this.buildItemForm();
  }

  startUpload($event, id?: string): void {
    const base = this;
    if (this.uploader.queue.length > 0) {
      setTimeout(() => {
        base.uploader.uploadAll();
      }, 400);
    }
  }

  uploaderUpdate() {
    const base = this;
    const docUrl: any = this.taxStrategyServices.getVideoDocumentServiceUrl(this.org, this.user);
    this.uploader = new FileUploader({
      url: docUrl,
      headers: [{
        name: 'Authorization',
        value: 'Bearer ' + Session.getToken()
      }],
      filters: [
        {
          name: 'questionnaireFileType',
          fn: (item: any): boolean => {
            const ext = (item.name || '').split('.').pop().toLowerCase();
            const type = (item.type || '').toLowerCase();
            // extension must be allowed; MIME must match when the browser set one
            // (HEIC photos often arrive with an empty type, so extension governs)
            return ALLOWED_EXTENSIONS.indexOf(ext) !== -1
              && (!type || ALLOWED_MIME_TYPES.indexOf(type) !== -1);
          }
        },
        {
          name: 'questionnaireFileSize',
          fn: (item: any): boolean => !item.size || item.size <= MAX_FILE_SIZE
        }
      ]
    } as FileUploaderOptions);
    this.uploader.onAfterAddingFile = file => {
      file.withCredentials = false;
    };
    // ng2-file-upload calls this whenever a filter rejects a file
    this.uploader.onWhenAddingFileFailed = (item: any, filter: any) => {
      const message = filter && filter.name === 'questionnaireFileSize'
        ? `${item.name} is larger than 25 MB`
        : `${item.name} is not an accepted file type (PDF, Word, or image only)`;
      this.toastService.pop(TOAST_TYPE.error, message);
    };
  }

  buildItemForm() {
    const base = this;
    this.uploader.onBuildItemForm = (fileItem: any, form: any) => {
      const payload: any = {};
      payload.sourceType = 'tax_planning_questionnaire';
      payload.sourceID = uuid();
      if (fileItem.file && fileItem.file.size){
        payload.fileSize = fileItem.file.size;
      }
      form.append('payload', JSON.stringify(payload));
    };
    this.uploader.onCompleteItem = (item, response, status, header) => {
      this.loadingService.triggerLoadingEvent(false);
      if (status === 200) {
        this.uploadCount++;
        this.uploader.progress = 100;
        const document: any = JSON.parse(response);
        const fileObj = {id: document.id, name: document.name, bucket: '', sourceId: base.id};
        this.uploadedFiles.push(fileObj);
        this.showProgress = true;
        if (this.uploader.queue.length === this.uploadCount) {
          this.uploader.queue = [];
          this.uploadCount = 0;
          this.showProgress = false;
          base.onFileUpload.emit(base.uploadedFiles);
        }
      } else {
        this.resetUploader();
        this.toastService.pop(TOAST_TYPE.error, 'Failed to upload document');
      }
    };
    this.uploader.onErrorItem = (item, response, status, headers) => {
      this.loadingService.triggerLoadingEvent(false);
    };
  }

  resetUploader(type?) {
    this.uploadCount = 0;
    this.uploader.queue.forEach(function(item) {
      item.remove();
    });
  }

  fileEvent(fileInput: any): void {
    this.selectedFiles = [...fileInput.target.files];
    for (let i = 0; i < this.selectedFiles.length; i++) {
      const file = this.selectedFiles[i];
      const fileName = file.name;
      this.totalSize += this.selectedFiles[i].size;
      ++this.totalFiles;
      if (this.selectedFiles.length - 1 === i){
        fileInput.target.value = '';
      }
      this.uploadFile(this.selectedFiles[i]);
      this.loaded[file.name] = 0;
    }
  }

  uploadFile(file): void {
    const base = this;
    const contentType = file.type;
    const fileId = this.getUUID();
    const fileName = encodeURI(file.name);
    const bucket = new S3(this.s3Data);
    const params = {
      Bucket: this.uploadBucket,
      Key: fileId,
      Body: file,
      ContentType: contentType,
      ContentDisposition: 'inline;filename="' + fileName + '"'
    };
    const fileObj = {id: fileId, name: file.name, bucket: base.uploadBucket, sourceId: base.id};
    this.uploadedFiles.push(fileObj);
    this.showProgress = true;
    bucket.upload(params).on('httpUploadProgress', function(evt) {
      base.loaded[this.body.name] = evt.loaded;
      let loadedTotal = 0;
      for (const j in base.loaded) {
        loadedTotal += base.loaded[j];
      }
      const uploaded = Math.round(loadedTotal / base.totalSize * 100);
      base.progressWidth = uploaded;
      base.changeDetectorRef.detectChanges();

    }).send((err) => {
      if (err) {
        return;
      }
      ++base.bulkUploadCount;
      if (base.bulkUploadCount === base.totalFiles){
        base.progressWidth = 100;
        base.showProgress = false;
        base.onFileUpload.emit(base.uploadedFiles);
        base.resetUpload();
      }
    });
  }

  resetUpload(): void {
    this.totalFiles = 0;
    this.progressWidth = 0;
    this.totalSize = 0;
    this.bulkUploadCount = 0;
    this.loaded = [];
    this.showProgress = false;
    this.selectedFiles = [];
    this.uploadedFiles = [];
  }

  getUUID(): string {
    return uuid();
  }

  setS3Details(details): void {
    this.s3Data.accessKeyId = details.access_key;
    this.s3Data.secretAccessKey = details.secret_key;
    this.s3Data.sessionToken = details.session_token;
    this.uploadBucket = details.bucket_id;
  }

}
